import {
  SlideCanvasPoint,
  SlideCanvasBox,
  ResizeHandle
} from '../components/SlideCanvas.types';
import { rotatePoint } from './slideRotation';

/**
 * Logic: Resize Action (Natural Space)
 * 
 * For rotated boxes, standard AABB resizing doesn't work.
 * This implementation:
 * 1. Identifies the fixed opposite corner in global space.
 * 2. Rotates both the cursor and the fixed corner inversely around the box center.
 * 3. Performs axis-aligned scaling in local space.
 * 4. Repositions the box center globally to ensure the fixed corner doesn't move.
 */
export function applyResize(
  box: SlideCanvasBox,
  handle: ResizeHandle,
  currentPointNatural: SlideCanvasPoint
): SlideCanvasBox {
  if (!handle) return { ...box };

  const center = {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2
  };

  const cornersLocal = {
    NorthWest: { x: box.x, y: box.y },
    NorthEast: { x: box.x + box.width, y: box.y },
    SouthWest: { x: box.x, y: box.y + box.height },
    SouthEast: { x: box.x + box.width, y: box.y + box.height }
  };

  const oppositeMap = {
    NorthWest: 'SouthEast',
    NorthEast: 'SouthWest',
    SouthWest: 'NorthEast',
    SouthEast: 'NorthWest'
  };

  const fixedCornerLocal = cornersLocal[oppositeMap[handle] as keyof typeof cornersLocal];

  // 1. Transform cursor to box-local coordinates.
  const localCursor = rotatePoint(currentPointNatural, center, -box.rotation);

  // 3. Perform axis-aligned scaling in local space.
  const newWidth = Math.max(1, Math.abs(localCursor.x - fixedCornerLocal.x));
  const newHeight = Math.max(1, Math.abs(localCursor.y - fixedCornerLocal.y));

  // 4. Find the actual new corner in local space (respecting min size).
  const directionX = localCursor.x >= fixedCornerLocal.x ? 1 : -1;
  const directionY = localCursor.y >= fixedCornerLocal.y ? 1 : -1;
  const actualLocalCorner = {
    x: fixedCornerLocal.x + directionX * newWidth,
    y: fixedCornerLocal.y + directionY * newHeight
  };

  // 5. Compute new center in global space.
  const localCenter = {
    x: (fixedCornerLocal.x + actualLocalCorner.x) / 2,
    y: (fixedCornerLocal.y + actualLocalCorner.y) / 2
  };
  const newCenterGlobal = rotatePoint(localCenter, center, box.rotation);

  return {
    ...box,
    width: newWidth,
    height: newHeight,
    x: newCenterGlobal.x - newWidth / 2,
    y: newCenterGlobal.y - newHeight / 2,
    rotation: box.rotation
  };
}
